import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import api from "../lib/api";

const PR_COLOR = { high: "#EF4444", medium: "#F59E0B", low: "#EAB308" };

// Compact, section-scoped action summary. Tells the admin what needs attention
// in THIS section only — complements (does not duplicate) the dashboard Action Center.
export default function AdminSectionSummary({ section, reportCategory, onFilterReports }) {
  const [task, setTask] = useState(undefined);
  const [cats, setCats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/action-center");
      setTask((data.tasks || []).find((t) => t.section === section) || null);
    } catch (e) { setTask(null); }
    if (section === "reports") {
      try { const r = await api.get("/admin/reports/summary"); setCats(r.data.categories || []); }
      catch (e) { setCats([]); }
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("admin-stats-refresh", h);
    return () => window.removeEventListener("admin-stats-refresh", h);
  }, [load]);

  if (task === undefined) return null;

  const color = task ? PR_COLOR[task.priority] : "#2E7D52";

  return (
    <div data-testid={`admin-section-summary-${section}`} className="rounded-xl border bg-[#141113]/60 px-4 py-3 mb-5"
      style={{ borderColor: task ? `${color}55` : "rgba(46,125,82,0.3)" }}>
      <div className="flex items-center gap-2.5">
        {task ? <AlertTriangle size={16} style={{ color }} /> : <CheckCircle2 size={16} className="text-[#6FD79E]" />}
        <span className="text-sm text-[#FBF8F3]">
          {task
            ? <><span className="font-semibold" style={{ color }}>{task.count}</span> {task.label.toLowerCase().replace("pending ", "").replace("awaiting approval", "awaiting approval")} — {task.explanation}</>
            : "Nothing needs attention in this section right now."}
        </span>
      </div>

      {section === "reports" && cats && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="reports-category-chips">
          <button data-testid="report-cat-all" onClick={() => onFilterReports && onFilterReports(null)}
            className={`rounded-full px-3 py-1 text-xs border ${!reportCategory ? "bg-[#541224] border-[#D4AF37] text-[#FBF8F3]" : "bg-[#1C171A] border-white/10 text-[#C9A96E] hover:border-[#D4AF37]/50"}`}>
            All ({cats.reduce((s, c) => s + c.count, 0)})
          </button>
          {cats.map((c) => (
            <button key={c.key} data-testid={`report-cat-${c.key}`} onClick={() => onFilterReports && onFilterReports(c.key)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border ${reportCategory === c.key ? "bg-[#541224] border-[#D4AF37] text-[#FBF8F3]" : "bg-[#1C171A] border-white/10 text-[#C9A96E] hover:border-[#D4AF37]/50"}`}>
              {c.label} <span className={c.count > 0 ? "text-[#E0B84A] font-semibold" : "text-[#9E978E]"}>({c.count})</span>
              {reportCategory === c.key && <ChevronRight size={11} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
